import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import { BookingCalendar } from '../home/components/BookingCalendar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';

const CANONICAL = 'https://hoosierlifeinsurance.com/guaranteed-issue-life-insurance-indianapolis';
const SITE_URL = 'https://hoosierlifeinsurance.com';

type GIAge = '50' | '55' | '60' | '65' | '70' | '75' | '80' | '85';
type GICoverage = '5k' | '10k' | '15k' | '20k' | '25k';

interface GIResult {
  carrier: string;
  amBest: string;
  monthlyRate: number;
  waitingPeriod: string;
  maxAge: number;
  maxCoverage: string;
  returnOfPremium: boolean;
  notes: string;
  badge?: string;
}

const GI_BASE: Record<GIAge, Record<GICoverage, number>> = {
  '50': { '5k': 18, '10k': 32, '15k': 46, '20k': 60, '25k': 74 },
  '55': { '5k': 22, '10k': 40, '15k': 58, '20k': 76, '25k': 94 },
  '60': { '5k': 28, '10k': 52, '15k': 76, '20k': 100, '25k': 124 },
  '65': { '5k': 36, '10k': 68, '15k': 100, '20k': 132, '25k': 164 },
  '70': { '5k': 48, '10k': 92, '15k': 136, '20k': 180, '25k': 224 },
  '75': { '5k': 64, '10k': 124, '15k': 184, '20k': 244, '25k': 304 },
  '80': { '5k': 88, '10k': 172, '15k': 256, '20k': 340, '25k': 424 },
  '85': { '5k': 118, '10k': 232, '15k': 346, '20k': 460, '25k': 574 },
};

const COVERAGE_LABELS: Record<GICoverage, string> = {
  '5k': '$5,000', '10k': '$10,000', '15k': '$15,000', '20k': '$20,000', '25k': '$25,000',
};

function getGIResults(age: GIAge, coverage: GICoverage): GIResult[] {
  const base = GI_BASE[age][coverage];
  return [
    {
      carrier: 'Mutual of Omaha',
      amBest: 'A+',
      monthlyRate: base,
      waitingPeriod: '2 years',
      maxAge: 85,
      maxCoverage: '$25,000',
      returnOfPremium: true,
      notes: 'Best guaranteed issue carrier in Indianapolis. Return of premium if death occurs in waiting period. Accepts ages 45–85.',
      badge: 'Best Overall',
    },
    {
      carrier: 'AIG / Corebridge',
      amBest: 'A',
      monthlyRate: Math.round(base * 1.05),
      waitingPeriod: '2 years',
      maxAge: 80,
      maxCoverage: '$25,000',
      returnOfPremium: true,
      notes: 'Strong second option. Competitive rates for ages 50–75. Return of premium during waiting period.',
    },
    {
      carrier: 'Gerber Life',
      amBest: 'A',
      monthlyRate: Math.round(base * 1.08),
      waitingPeriod: '2 years',
      maxAge: 80,
      maxCoverage: '$25,000',
      returnOfPremium: false,
      notes: 'Well-known brand. Slightly higher rates but strong name recognition. Good for Indianapolis seniors who prefer familiar brands.',
    },
    {
      carrier: 'Colonial Penn',
      amBest: 'A-',
      monthlyRate: Math.round(base * 1.12),
      waitingPeriod: '2 years',
      maxAge: 85,
      maxCoverage: '$50,000',
      returnOfPremium: false,
      notes: 'Higher coverage limits available. Rates are higher per unit but max coverage extends to $50K for Indianapolis applicants.',
    },
    {
      carrier: 'Transamerica',
      amBest: 'A',
      monthlyRate: Math.round(base * 1.15),
      waitingPeriod: '2 years',
      maxAge: 80,
      maxCoverage: '$25,000',
      returnOfPremium: true,
      notes: 'Solid option for Indianapolis seniors. Return of premium during waiting period. Good for those with serious health conditions.',
    },
  ];
}

const WHO_QUALIFIES = [
  { condition: 'Cancer (any type)', eligible: true, note: 'Guaranteed approval — no health questions' },
  { condition: 'Heart disease / heart attack', eligible: true, note: 'Guaranteed approval — no health questions' },
  { condition: 'Stroke history', eligible: true, note: 'Guaranteed approval — no health questions' },
  { condition: 'COPD / emphysema', eligible: true, note: 'Guaranteed approval — no health questions' },
  { condition: 'Kidney disease / dialysis', eligible: true, note: 'Guaranteed approval — no health questions' },
  { condition: 'Diabetes (any type)', eligible: true, note: 'Guaranteed approval — no health questions' },
  { condition: 'HIV / AIDS', eligible: true, note: 'Guaranteed approval — no health questions' },
  { condition: 'Alzheimer\'s / dementia', eligible: true, note: 'Guaranteed approval — no health questions' },
  { condition: 'Oxygen use', eligible: true, note: 'Guaranteed approval — no health questions' },
  { condition: 'Nursing home / assisted living', eligible: false, note: 'Most carriers require independent living' },
];

const CLUSTER_LINKS = [
  { to: '/final-expense-insurance-quotes-indianapolis', label: 'Final Expense Insurance Quotes Indianapolis', icon: 'ri-heart-line' },
  { to: '/senior-life-insurance-indianapolis', label: 'Senior Life Insurance Indianapolis', icon: 'ri-user-heart-line' },
  { to: '/life-insurance-for-seniors-over-60-indianapolis', label: 'Life Insurance for Seniors Over 60', icon: 'ri-calendar-line' },
  { to: '/burial-insurance-indianapolis', label: 'Burial Insurance Indianapolis', icon: 'ri-home-heart-line' },
  { to: '/no-exam-life-insurance-indianapolis', label: 'No Exam Life Insurance Indianapolis', icon: 'ri-stethoscope-line' },
  { to: '/life-insurance-for-diabetics-indianapolis', label: 'Life Insurance for Diabetics Indianapolis', icon: 'ri-heart-pulse-line' },
  { to: '/life-insurance-smokers-indianapolis', label: 'Life Insurance for Smokers Indianapolis', icon: 'ri-forbid-line' },
  { to: '/affordable-life-insurance-indianapolis', label: 'Affordable Life Insurance Indianapolis', icon: 'ri-money-dollar-circle-line' },
  { to: '/cheap-life-insurance-indianapolis', label: 'Cheap Life Insurance Indianapolis', icon: 'ri-price-tag-3-line' },
  { to: '/life-insurance-agent-indianapolis', label: 'Life Insurance Agent Indianapolis', icon: 'ri-user-star-line' },
  { to: '/life-insurance-quotes-indianapolis', label: 'Life Insurance Quotes Indianapolis', icon: 'ri-search-line' },
  { to: '/best-life-insurance-indianapolis', label: 'Best Life Insurance Indianapolis', icon: 'ri-award-line' },
];

const FAQS = [
  {
    q: 'What is guaranteed issue life insurance in Indianapolis?',
    a: 'Guaranteed issue life insurance in Indianapolis is a type of whole life insurance that approves every applicant regardless of health — no medical exam, no health questions, no medical records review. If you are between ages 45–85 and live in Indianapolis or Indiana, you are guaranteed to be approved. The trade-off: coverage is limited (typically $5,000–$25,000), premiums are higher than traditional life insurance, and there is a 2-year waiting period before the full death benefit is paid. Guaranteed issue life insurance is designed for Indianapolis seniors and individuals with serious health conditions who cannot qualify for traditional life insurance.',
  },
  {
    q: 'Who should get guaranteed issue life insurance in Indianapolis?',
    a: 'Guaranteed issue life insurance in Indianapolis is the right choice for: 1. Indianapolis seniors with serious health conditions (cancer, heart disease, stroke, COPD, kidney disease) who have been declined for traditional life insurance. 2. Individuals who need coverage quickly and cannot wait for medical underwriting. 3. Indianapolis residents who want to cover final expenses (funeral, burial, medical bills) without burdening their family. 4. People who have exhausted other options (term life, simplified issue) due to health conditions. If you are in good health, you should explore term life or simplified issue life insurance first — they offer much better rates and higher coverage amounts.',
  },
  {
    q: 'What is the 2-year waiting period for guaranteed issue life insurance?',
    a: 'The 2-year waiting period (also called the graded death benefit period) means that if you die within the first 2 years of your guaranteed issue policy, your beneficiary will not receive the full death benefit. Instead, they will receive a return of all premiums paid, plus interest (typically 10%). After 2 years, the full death benefit is paid for any cause of death. Example: If you pay $50/month for 18 months and then pass away, your beneficiary receives $900 (18 months × $50) plus 10% interest = $990. After 24 months, they would receive the full $10,000 death benefit. The waiting period exists because guaranteed issue policies accept everyone — including those who are terminally ill.',
  },
  {
    q: 'How much does guaranteed issue life insurance cost in Indianapolis?',
    a: 'Guaranteed issue life insurance rates in Indianapolis vary by age and coverage amount. Here are sample monthly rates for Mutual of Omaha (the best carrier): Age 60, $10,000 coverage: $52/month. Age 65, $10,000 coverage: $68/month. Age 70, $10,000 coverage: $92/month. Age 75, $10,000 coverage: $124/month. Age 80, $10,000 coverage: $172/month. These rates are higher than traditional life insurance because the carrier accepts all applicants regardless of health. An independent agent can compare all available carriers to find the lowest guaranteed issue rate for your age and coverage needs.',
  },
  {
    q: 'What is the best guaranteed issue life insurance carrier in Indianapolis?',
    a: 'The best guaranteed issue life insurance carrier in Indianapolis is Mutual of Omaha. Here\'s why: 1. AM Best A+ rating — the highest financial strength rating. 2. Return of premium during the 2-year waiting period — if you die in the first 2 years, your family gets all premiums back plus 10% interest. 3. Accepts ages 45–85 — the widest age range available. 4. Coverage up to $25,000. 5. Competitive rates — consistently the lowest guaranteed issue rates in Indianapolis. Other strong options include AIG/Corebridge (A rated) and Transamerica (A rated). An independent agent can compare all available carriers to find the best guaranteed issue policy for your specific situation.',
  },
  {
    q: 'Is guaranteed issue life insurance worth it in Indianapolis?',
    a: 'Guaranteed issue life insurance is worth it in Indianapolis if you have serious health conditions and cannot qualify for traditional life insurance. Here\'s how to think about it: If you are in poor health and have been declined for traditional life insurance, guaranteed issue is often your only option for coverage. The cost is higher, but the alternative is leaving your family with no coverage at all. If you are in good health, guaranteed issue is NOT worth it — you can get much better rates and higher coverage with term life or simplified issue life insurance. The key question: Can you qualify for simplified issue life insurance? Simplified issue has no medical exam but does ask health questions. If you can answer "no" to the key health questions, simplified issue offers better rates and no waiting period.',
  },
  {
    q: 'What is the difference between guaranteed issue and simplified issue life insurance?',
    a: 'Guaranteed issue life insurance: No health questions, no medical exam, guaranteed approval for all applicants ages 45–85. 2-year waiting period. Coverage limited to $5,000–$25,000. Higher premiums. Simplified issue life insurance: No medical exam, but does ask health questions (typically 5–15 questions). Approval is not guaranteed — applicants with serious conditions may be declined. No waiting period (or shorter waiting period). Coverage up to $50,000–$100,000. Lower premiums than guaranteed issue. Which is right for you? If you have serious health conditions (cancer, heart disease, stroke, COPD, kidney disease), you likely need guaranteed issue. If you have controlled conditions (diabetes, high blood pressure, high cholesterol), you may qualify for simplified issue at better rates. An independent agent can help you determine which option is best for your situation.',
  },
  {
    q: 'Can I get guaranteed issue life insurance in Indianapolis if I have cancer?',
    a: 'Yes — guaranteed issue life insurance in Indianapolis approves all applicants regardless of health, including those with cancer. There are no health questions and no medical exam. Whether you have active cancer, are in remission, or have a history of cancer, you are guaranteed to be approved as long as you are between ages 45–85. The trade-off: the 2-year waiting period applies. If you pass away from cancer within the first 2 years of the policy, your beneficiary will receive a return of premiums paid plus 10% interest — not the full death benefit. After 2 years, the full death benefit is paid regardless of cause of death. Call (317) 648-2983 to get a guaranteed issue quote for Indianapolis cancer patients.',
  },
  {
    q: 'How do I apply for guaranteed issue life insurance in Indianapolis?',
    a: 'Applying for guaranteed issue life insurance in Indianapolis is simple: 1. Call Hoosier Life Insurance at (317) 648-2983 or book a free consultation online. 2. We compare all available guaranteed issue carriers (Mutual of Omaha, AIG, Gerber Life, Colonial Penn, Transamerica) to find the lowest rate for your age and coverage needs. 3. Complete a simple application — no medical exam, no health questions, just basic personal information. 4. Receive your policy within 1–2 weeks. The entire process takes about 15–30 minutes. There is no medical exam, no waiting for lab results, and no risk of being declined. Call (317) 648-2983 to get started today.',
  },
];

const TESTIMONIALS = [
  {
    name: 'Dorothy M.',
    location: 'Indianapolis, IN',
    quote: 'I was diagnosed with stage 3 cancer and thought I\'d never get life insurance again. Benjamin found me a $15,000 guaranteed issue policy with Mutual of Omaha. My family won\'t have to worry about funeral costs.',
    stars: 5,
    coverage: '$15,000 guaranteed issue',
    age: 'Age 68',
  },
  {
    name: 'Harold & Ruth K.',
    location: 'Carmel, Indianapolis',
    quote: 'My husband had a heart attack two years ago and was declined everywhere. Benjamin got him approved for $20,000 with no health questions. The process took 20 minutes. We\'re so relieved.',
    stars: 5,
    coverage: '$20,000 guaranteed issue',
    age: 'Age 72',
  },
  {
    name: 'Patricia L.',
    location: 'Fishers, Indianapolis',
    quote: 'I have COPD and was told I was uninsurable. Benjamin found me guaranteed issue coverage for $52/month. My kids won\'t have to pay for my funeral. That peace of mind is priceless.',
    stars: 5,
    coverage: '$10,000 guaranteed issue',
    age: 'Age 64',
  },
  {
    name: 'James T.',
    location: 'Noblesville, Indianapolis',
    quote: 'Dialysis three times a week. Every carrier turned me down. Mutual of Omaha approved me in 15 minutes with no health questions. Benjamin made it so easy. Highly recommend.',
    stars: 5,
    coverage: '$15,000 guaranteed issue',
    age: 'Age 71',
  },
];

export default function GuaranteedIssueLifeInsuranceIndianapolisPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [giAge, setGIAge] = useState<GIAge>('65');
  const [giCoverage, setGICoverage] = useState<GICoverage>('10k');
  const [results, setResults] = useState<GIResult[] | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const title = 'Guaranteed Issue Life Insurance Indianapolis IN | No Health Questions | Hoosier Life Insurance';
    const description = 'Guaranteed issue life insurance in Indianapolis, IN. Approved regardless of health — cancer, heart disease, COPD, diabetes. No medical exam. Ages 45–85. Call (317) 648-2983.';
    const keywords = 'guaranteed issue life insurance Indianapolis, guaranteed acceptance life insurance Indianapolis, no health questions life insurance Indianapolis, life insurance with health conditions Indianapolis';
    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'last-modified', content: new Date().toISOString() },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: CANONICAL },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://public.readdy.ai/ai/img_res/a81386bc37b73e1631630fcfb1bf0f6c.jpg' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'geo.position', content: '39.7684;-86.1581' },
      { name: 'geo.placename', content: 'Indianapolis, Indiana' },
      { name: 'geo.region', content: 'US-IN' },
    ];

    const addedMetas: HTMLMetaElement[] = [];
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.setAttribute('name', tag.name);
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
      addedMetas.push(meta);
    });

    const linkCanonical = document.createElement('link');
    linkCanonical.rel = 'canonical';
    linkCanonical.href = CANONICAL;
    document.head.appendChild(linkCanonical);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
        url: CANONICAL,
        inLanguage: 'en-US',
        dateModified: '2026-04-14T00:00:00Z',
        isPartOf: { '@type': 'WebSite', name: 'Hoosier Life Insurance', url: SITE_URL },
        speakable: {
          '@type': 'SpeakableSpecification',
          xpath: ['/html/body//*[@id="gi-finder"]'],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Life Insurance Indianapolis', item: `${SITE_URL}/life-insurance-indianapolis` },
          { '@type': 'ListItem', position: 3, name: 'Guaranteed Issue Life Insurance Indianapolis', item: CANONICAL },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
    ];

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schemas);
    document.head.appendChild(scriptTag);

    return () => {
      addedMetas.forEach(m => { if (document.head.contains(m)) document.head.removeChild(m); });
      if (document.head.contains(linkCanonical)) document.head.removeChild(linkCanonical);
      if (document.head.contains(scriptTag)) document.head.removeChild(scriptTag);
    };
  }, []);

  const ageOptions: GIAge[] = ['50', '55', '60', '65', '70', '75', '80', '85'];
  const coverageOptions: { value: GICoverage; label: string }[] = [
    { value: '5k', label: '$5,000' },
    { value: '10k', label: '$10,000' },
    { value: '15k', label: '$15,000' },
    { value: '20k', label: '$20,000' },
    { value: '25k', label: '$25,000' },
  ];

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-[#D97642] transition-colors flex items-center gap-1 whitespace-nowrap">
            <i className="ri-home-4-line text-sm"></i> Home
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <Link to="/life-insurance-indianapolis" className="text-gray-500 hover:text-[#D97642] transition-colors whitespace-nowrap">Life Insurance Indianapolis</Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-[#1A3A52] font-semibold whitespace-nowrap">Guaranteed Issue</span>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden min-h-[560px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=Indianapolis%20Indiana%20senior%20couple%20smiling%20warmly%20at%20home%20comfortable%20living%20room%20soft%20natural%20light%20warm%20cream%20beige%20tones%20peaceful%20relaxed%20financial%20security%20life%20insurance%20approval%20photorealistic%20professional%20photography%20wide%20angle&width=1440&height=700&seq=gi-li-indy-hero-2025a&orientation=landscape"
            alt="Guaranteed Issue Life Insurance Indianapolis Indiana — No Health Questions"
            title="Guaranteed Issue Life Insurance in Indianapolis, Indiana"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1440}
            height={700}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/96 via-[#1A3A52]/85 to-[#1A3A52]/30"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 w-full">
          <div className="max-w-[720px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <i className="ri-shield-check-line text-sm"></i>
              Indianapolis &amp; Marion County · Guaranteed Approval · No Health Questions · Ages 45–85
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Guaranteed Issue Life Insurance —{' '}
              <span className="text-[#D97642]">No Health Questions, Indianapolis</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-6 max-w-[640px]">
              Approved regardless of health — <strong className="text-white">no medical exam, no health questions, guaranteed acceptance</strong>. Cancer, heart disease, COPD, diabetes — all approved. Ages 45–85. Coverage from $5,000–$25,000.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {[
                { value: '100%', label: 'Guaranteed approval — no health questions asked' },
                { value: 'Ages 45–85', label: 'All ages accepted in Indianapolis, Indiana' },
                { value: '$25K', label: 'Maximum coverage available — no exam required' },
                { value: '15 min', label: 'Application time — simple and straightforward' },
              ].map(item => (
                <div key={item.label} className="bg-white/15 backdrop-blur-sm border border-white/25 rounded-xl px-4 py-3 text-center">
                  <div className="text-xl font-extrabold text-[#D97642]">{item.value}</div>
                  <div className="text-xs text-white/80 mt-0.5">{item.label}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <a href="#gi-finder" className="inline-flex items-center gap-2 px-7 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-shield-check-line text-lg"></i>
                Get My Guaranteed Rate
              </a>
              <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-7 py-4 bg-white/20 border border-white/40 text-white text-base font-semibold rounded-xl hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1A3A52] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { value: '100%', label: 'Guaranteed approval — no health questions, no medical exam, no records review' },
              { value: '5', label: 'Top guaranteed issue carriers compared for Indianapolis applicants' },
              { value: '2 yrs', label: 'Waiting period — after which full death benefit is paid for any cause' },
              { value: '$25K', label: 'Maximum guaranteed issue coverage available in Indianapolis' },
            ].map(stat => (
              <div key={stat.label} className="flex flex-col items-center gap-1">
                <span className="text-2xl md:text-3xl font-extrabold text-[#D97642]">{stat.value}</span>
                <span className="text-xs text-white/70 leading-snug max-w-[160px]">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Takeaways */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-checkbox-circle-fill text-[#D97642] text-2xl"></i>
            </div>
            <div className="flex-1">
              <h2 className="text-[#1A3A52] font-bold text-base mb-3">Key Facts: Guaranteed Issue Life Insurance in Indianapolis</h2>
              <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                {[
                  'Guaranteed approval for all Indianapolis applicants ages 45–85 — no health questions asked',
                  'No medical exam, no medical records review, no prescription history check',
                  'Cancer, heart disease, stroke, COPD, kidney disease, diabetes — all approved',
                  '2-year waiting period — if death occurs in first 2 years, premiums returned plus 10% interest',
                  'Mutual of Omaha is the best guaranteed issue carrier — A+ rated, return of premium',
                  'Coverage from $5,000–$25,000 — designed to cover funeral and final expenses',
                ].map((bullet, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-600 text-sm">
                    <i className="ri-arrow-right-s-line text-[#D97642] flex-shrink-0 mt-0.5"></i>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 text-gray-400 text-xs">Last reviewed April 2026 · Licensed Life Insurance Specialists at Hoosier Life Insurance · Indiana Licensed · Independent — 20+ Carriers</div>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN CONTENT + SIDEBAR */}
      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 xl:gap-16 items-start">
          <div>

            {/* GI RATE FINDER */}
            <div id="gi-finder" className="mb-16 scroll-mt-24">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Guaranteed Issue Rate Finder</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3 leading-tight">
                Compare Guaranteed Issue Rates — Indianapolis
              </h2>
              <p className="text-sm text-gray-500 mb-8">
                Select your age and coverage amount to see guaranteed issue rates from the top 5 carriers. All applicants ages 45–85 are guaranteed to be approved.
              </p>

              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-[#1A3A52] px-7 py-7">
                  <h3 className="text-base font-bold text-white mb-6 flex items-center gap-2">
                    <i className="ri-shield-check-line text-[#D97642]"></i>
                    Your Profile — Guaranteed Approval
                  </h3>

                  <div className="mb-5">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Your Age</label>
                    <div className="flex flex-wrap gap-2">
                      {ageOptions.map(a => (
                        <button key={a} onClick={() => { setGIAge(a); setResults(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${giAge === a ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          {a}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-7">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Coverage Amount</label>
                    <div className="flex flex-wrap gap-2">
                      {coverageOptions.map(opt => (
                        <button key={opt.value} onClick={() => { setGICoverage(opt.value); setResults(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${giCoverage === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="bg-[#4A7C59]/20 border border-[#4A7C59]/30 rounded-xl px-5 py-4 mb-7">
                    <div className="flex items-center gap-2 mb-1">
                      <i className="ri-shield-check-fill text-[#4A7C59] text-lg"></i>
                      <span className="text-sm font-bold text-white">Guaranteed Approval</span>
                    </div>
                    <p className="text-xs text-white/70">No health questions. No medical exam. All Indianapolis applicants ages 45–85 are guaranteed to be approved.</p>
                  </div>

                  <button onClick={() => setResults(getGIResults(giAge, giCoverage))}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold text-base">
                    <i className="ri-shield-check-line text-lg"></i>
                    See My Guaranteed Rates
                  </button>
                </div>

                {results !== null && (
                  <div className="px-7 py-7">
                    <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
                      <h3 className="text-base font-bold text-[#1A3A52]">
                        Guaranteed Issue Rates — {COVERAGE_LABELS[giCoverage]} · Age {giAge}
                      </h3>
                      <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#4A7C59]/10 text-[#4A7C59] whitespace-nowrap">
                        All Guaranteed Approved
                      </span>
                    </div>

                    <div className="flex flex-col gap-4 mb-6">
                      {results.map((r, i) => (
                        <div key={r.carrier} className={`rounded-xl border p-5 ${i === 0 ? 'border-[#4A7C59]/30 bg-[#F0FFF4]' : 'border-gray-100 bg-gray-50'}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 flex items-center justify-center rounded-full text-white text-sm font-bold flex-shrink-0 ${i === 0 ? 'bg-[#4A7C59]' : 'bg-[#1A3A52]/30'}`}>{i + 1}</div>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-bold text-[#1A3A52]">{r.carrier}</span>
                                  {r.badge && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#4A7C59] text-white whitespace-nowrap">{r.badge}</span>}
                                  <span className="text-xs bg-[#1A3A52]/10 text-[#1A3A52] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">AM Best {r.amBest}</span>
                                </div>
                                <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{r.notes}</p>
                                <div className="flex items-center gap-3 mt-1 flex-wrap">
                                  <span className="text-xs text-gray-400">Waiting period: {r.waitingPeriod}</span>
                                  <span className="text-xs text-gray-400">Max coverage: {r.maxCoverage}</span>
                                  {r.returnOfPremium && <span className="text-xs text-[#4A7C59] font-semibold">✓ Return of premium</span>}
                                </div>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className={`text-xl font-extrabold ${i === 0 ? 'text-[#4A7C59]' : 'text-[#1A3A52]'}`}>${r.monthlyRate}<span className="text-sm font-normal">/mo</span></div>
                              <div className="text-xs text-gray-400">${(r.monthlyRate * 12).toLocaleString()}/yr</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5">
                      <div className="flex items-start gap-2">
                        <i className="ri-information-line text-amber-500 text-base flex-shrink-0 mt-0.5"></i>
                        <p className="text-xs text-amber-700 leading-relaxed">
                          <strong>2-Year Waiting Period:</strong> If death occurs in the first 2 years, beneficiaries receive all premiums paid plus 10% interest. After 2 years, the full death benefit is paid for any cause of death.
                        </p>
                      </div>
                    </div>

                    <div className="bg-[#1A3A52]/5 rounded-xl p-4 mb-5">
                      <p className="text-xs text-gray-500 leading-relaxed">
                        <strong className="text-[#1A3A52]">Disclaimer:</strong> Rates are estimates for illustration purposes. Actual rates depend on carrier selection and state regulations. Call <a href="tel:+13176482983" className="text-[#D97642] font-semibold">(317) 648-2983</a> for your exact personalized rate.
                      </p>
                    </div>

                    <button onClick={() => setShowBookingModal(true)}
                      className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                      <i className="ri-calendar-check-line text-lg"></i>
                      Apply Now — Guaranteed Approval · Free 15-Min Call
                    </button>
                  </div>
                )}

                {results === null && (
                  <div className="px-7 py-10 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D97642]/10 mx-auto mb-4">
                      <i className="ri-shield-check-line text-3xl text-[#D97642]"></i>
                    </div>
                    <p className="text-sm text-gray-500">Select your age and coverage above, then click <strong className="text-[#1A3A52]">See My Guaranteed Rates</strong>.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Who Qualifies */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Eligibility</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Who Qualifies for Guaranteed Issue Life Insurance in Indianapolis?
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Guaranteed issue life insurance approves all Indianapolis applicants ages 45–85 regardless of health. Here are common conditions that are automatically approved:
              </p>
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="grid grid-cols-[1fr_auto_1fr] bg-[#1A3A52] px-6 py-3 text-xs font-bold text-white/70 uppercase tracking-wide">
                  <span>Health Condition</span>
                  <span className="text-center">Eligible?</span>
                  <span className="text-right">Notes</span>
                </div>
                {WHO_QUALIFIES.map((item, i) => (
                  <div key={i} className={`grid grid-cols-[1fr_auto_1fr] px-6 py-4 border-b border-gray-100 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <span className="text-sm font-semibold text-[#1A3A52]">{item.condition}</span>
                    <span className={`text-center text-sm font-bold px-3 ${item.eligible ? 'text-[#4A7C59]' : 'text-red-500'}`}>
                      {item.eligible ? '✓ Yes' : '✗ No'}
                    </span>
                    <span className="text-xs text-gray-500 text-right">{item.note}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Testimonials */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Real Stories</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Indianapolis Residents Who Got Guaranteed Coverage
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {TESTIMONIALS.map(t => (
                  <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100">
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <span className="bg-[#D97642]/10 text-[#D97642] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.coverage}</span>
                      <span className="bg-[#4A7C59]/10 text-[#4A7C59] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.age}</span>
                    </div>
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.stars }).map((_, i) => <i key={i} className="ri-star-fill text-[#D97642] text-sm"></i>)}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 italic">"{t.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                        <i className="ri-user-fill text-sm text-[#1A3A52]"></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#1A3A52]">{t.name}</p>
                        <p className="text-xs text-gray-400">{t.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Internal Linking Cluster */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Indianapolis Life Insurance Guide</span>
              <h2 className="text-2xl font-bold text-[#1A3A52] mb-4 leading-tight">Explore All Indianapolis Life Insurance Topics</h2>
              <p className="text-sm text-gray-500 mb-6">Compare rates, explore coverage options, and find the right policy for your Indianapolis family.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {CLUSTER_LINKS.map(link => (
                  <Link key={link.to} to={link.to} className="flex items-center gap-3 px-4 py-3.5 bg-white rounded-xl border border-gray-100 hover:border-[#D97642]/30 hover:bg-[#FFF8F4] transition-all group cursor-pointer">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-[#1A3A52]">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div id="faq">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Guaranteed Issue Life Insurance Indianapolis — Frequently Asked Questions
              </h2>
              <div className="flex flex-col gap-3">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <button onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors">
                      <h3 className="text-sm font-bold text-[#1A3A52] pr-4 leading-snug"><a id={`faq-gi-indy-${i}`}>{faq.q}</a></h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-[#D97642]`}></i>
                      </div>
                    </button>
                    {openFAQ === i && (
                      <div className="px-6 pb-5">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* SIDEBAR */}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-shield-check-fill text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight">Guaranteed Approval</h3>
                    <p className="text-xs text-white/70">No health questions · Ages 45–85</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ 100% guaranteed approval</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ No medical exam</span>
                </div>
              </div>
              <div className="px-7 py-7">
                <div className="flex flex-col gap-3">
                  <button onClick={() => setShowBookingModal(true)}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                    <i className="ri-calendar-check-line text-lg"></i>
                    Book Free Consultation
                  </button>
                  <a href="tel:+13176482983"
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1A3A52] text-white rounded-xl hover:bg-[#0F2A3F] transition-all cursor-pointer font-bold">
                    <i className="ri-phone-fill text-lg"></i>
                    Call (317) 648-2983
                  </a>
                </div>
                <p className="text-xs text-gray-400 text-center mt-4">Free · No obligation · All health conditions welcome</p>
              </div>
            </div>

            {/* Sample Rates */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4 flex items-center gap-2">
                <i className="ri-price-tag-3-line text-[#D97642]"></i>
                Sample Guaranteed Issue Rates
              </h3>
              <div className="flex flex-col gap-2">
                {[
                  { label: 'Age 60, $10,000', rate: '$52/mo', carrier: 'Mutual of Omaha' },
                  { label: 'Age 65, $10,000', rate: '$68/mo', carrier: 'Mutual of Omaha' },
                  { label: 'Age 70, $10,000', rate: '$92/mo', carrier: 'Mutual of Omaha' },
                  { label: 'Age 75, $10,000', rate: '$124/mo', carrier: 'Mutual of Omaha' },
                  { label: 'Age 65, $25,000', rate: '$164/mo', carrier: 'Mutual of Omaha' },
                  { label: 'Age 70, $25,000', rate: '$224/mo', carrier: 'Mutual of Omaha' },
                ].map((row, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <div className="text-xs font-semibold text-[#1A3A52]">{row.label}</div>
                      <div className="text-xs text-gray-400">{row.carrier}</div>
                    </div>
                    <span className="text-sm font-bold text-[#4A7C59]">{row.rate}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-3">Guaranteed approval. 2-year waiting period applies. Return of premium if death in first 2 years.</p>
            </div>

            {/* Booking Calendar */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-lg text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white leading-tight">Free 15-Min Consultation</h3>
                    <p className="text-xs text-white/70">Guaranteed approval · No pressure</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-6">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Book Your Free Consultation',
                    step0Title: 'Pick a Date', step1Title: 'Choose a Time', step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date', selectTime: 'See Available Times →', reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev', nextMonth: 'Next', noSlotsAvailable: 'No slots available',
                    selectedDateTime: 'Your Appointment', modifyTime: 'Change',
                    customerName: 'Full Name', customerPhone: 'Phone Number',
                    notes: 'Health conditions or coverage questions? (Optional)',
                    submitButton: 'Confirm My Free Consultation', submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.', backButton: '← Back to Calendar', required: '*',
                  }}
                  containerStyle=""
                />
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4">Related Pages</h3>
              <div className="flex flex-col gap-2">
                {[
                  { to: '/final-expense-insurance-quotes-indianapolis', label: 'Final Expense Quotes', icon: 'ri-heart-line' },
                  { to: '/senior-life-insurance-indianapolis', label: 'Senior Life Insurance', icon: 'ri-user-heart-line' },
                  { to: '/burial-insurance-indianapolis', label: 'Burial Insurance Indianapolis', icon: 'ri-home-heart-line' },
                  { to: '/no-exam-life-insurance-indianapolis', label: 'No Exam Life Insurance', icon: 'ri-stethoscope-line' },
                  { to: '/life-insurance-for-diabetics-indianapolis', label: 'Life Insurance for Diabetics', icon: 'ri-heart-pulse-line' },
                ].map(link => (
                  <Link key={link.to} to={link.to} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F9F7F4] transition-colors group">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm text-gray-600 group-hover:text-[#1A3A52] font-medium">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#1A3A52] py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Guaranteed approval — regardless of your health history
          </h2>
          <p className="text-base text-white/75 mb-7">
            Cancer, heart disease, COPD, diabetes — all approved. No medical exam. No health questions. Indianapolis applicants ages 45–85 are guaranteed to qualify.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#gi-finder" className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-shield-check-line text-lg"></i>
              Get My Guaranteed Rate
            </a>
            <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 border border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-lg"></i>
              (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={143} productName="Guaranteed Issue Life Insurance Indianapolis" />
      <Footer />
      <MobileProductCTABar productName="Guaranteed Issue Life Insurance" quoteHref="/instant-quote" />

      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#1A3A52]">Guaranteed Issue Life Insurance — Indianapolis</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>
            <div className="p-6">
              <iframe src="https://api.leadconnectorhq.com/widget/bookings/hoosier-life-insurance" style={{ width: '100%', height: '600px', border: 'none' }} title="Book Appointment" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
